import { useCallback, useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';

import { Box } from '@cast/design-system';
import {
  isDefaultOrganizationName,
  useCurrentOrganization,
} from '@cast/infrastructure-organization';
import { Page } from '@cast/shared-layout-ui';

import { useHasConnectedEnvironment } from '@oneclick/hooks/useHasConnectedEnvironment';

import { SuspenseLoader } from 'components/loaders';

import { ONBOARDING_PAGE_CARD_MAX_WIDTH } from './_components/CardShell';
import { ConnectionChoice } from './_constants';
import { Step1 } from './steps/Step1';
import { Step2 } from './steps/Step2';

const CONNECTION_METHODS: Record<string, ConnectionChoice> = {
  cloud: ConnectionChoice.cloudConnect,
  snapshot: ConnectionChoice.snapshot,
  single: ConnectionChoice.single,
};

export const OnboardingPage = () => {
  const currentOrganization = useCurrentOrganization();
  const {
    isLoading: isConnectionStatusLoading,
    hasConnectedEnvironment,
    isStatusUnknown,
  } = useHasConnectedEnvironment();
  const [searchParams, setSearchParams] = useSearchParams();
  const isStep2 = searchParams.get('step') === '2';
  const method = searchParams.get('method');
  const initialChoice =
    CONNECTION_METHODS[method ?? ''] ?? ConnectionChoice.cloudConnect;

  const skipOrgSetup = !isDefaultOrganizationName(currentOrganization?.name);

  useEffect(() => {
    if (!skipOrgSetup || isStep2) {
      return;
    }

    const nextParams: Record<string, string> = { step: '2' };
    if (method) {
      nextParams.method = method;
    }
    setSearchParams(nextParams, { replace: true });
  }, [isStep2, method, setSearchParams, skipOrgSetup]);

  const goToStep2 = useCallback(() => {
    setSearchParams({ step: '2' }, { replace: true });
  }, [setSearchParams]);

  const goToStep1 = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  if (!currentOrganization || isConnectionStatusLoading || isStatusUnknown) {
    return <SuspenseLoader />;
  }

  if (hasConnectedEnvironment) {
    return <Navigate to="/clusters" replace />;
  }

  if (skipOrgSetup && !isStep2) {
    return <SuspenseLoader />;
  }

  return (
    <Page
      analytics="Onboarding"
      useDsPage
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: 'auto',
        minHeight: '100%',
      }}
    >
      <Box sx={{ width: '100%', maxWidth: ONBOARDING_PAGE_CARD_MAX_WIDTH }}>
        {isStep2 ? (
          <Step2
            initialChoice={initialChoice}
            onBack={skipOrgSetup ? undefined : goToStep1}
            cardTotalSteps={skipOrgSetup ? 2 : 3}
          />
        ) : (
          <Step1 onNext={goToStep2} />
        )}
      </Box>
    </Page>
  );
};
